import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ItemTypes,
  ItemWithMetaData,
  ItemsForReviewRequest,
  ItemsForReviewResponse,
} from "../api/Items";
import { useFetchItemsForReview } from "../api/ItemsApi";
import { ReportSummaryItemApi, SourceTypeFilter } from "../api/Reports";
import { usePostManualEditDone } from "../api/WorkflowApi";
import AddPost, { OnCloseParams } from "../posts/AddPost";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../shared-components/Button";
import ErrorMessageContainer from "../shared-components/ErrorMessageContainer";
import { Modal } from "../shared-components/Modal";
import Pagination from "../shared-components/Pagination";
import AmendmentEditPosts from "./AmendmentEditPosts";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { useReportDetail } from "../reports/ReportDetailProvider";
import { PlusIcon } from "@heroicons/react/24/outline";

type PostDetailsProps = {
  isReadOnly: boolean;
  sourceTypeFilter: SourceTypeFilter;
  postTypeFilter: { original: boolean; shared: boolean; liked: boolean };
  dateFilter: { fromDate: string; toDate: string };
  behaviorFilter: string[];
  keywordFilter: string[];
  topicFilter: string[];
  report?: ReportSummaryItemApi;
  behaviors?: string[];
};

const PostDetails = ({
  sourceTypeFilter,
  postTypeFilter,
  dateFilter,
  behaviorFilter,
  keywordFilter,
  topicFilter,
  report,
  isReadOnly,
}: PostDetailsProps) => {
  const { id } = useParams();
  const { blueprint } = useReportDetail();
  const navigate = useNavigate();
  const {
    isLoading: postsLoading,
    isSuccess: postSuccess,
    data: initialPostList,
    mutate: getPosts,
  } = useFetchItemsForReview();
  const [postList, setPostList] = useState<ItemsForReviewResponse>();

  const {
    isLoading: isLoadingManualEditDone,
    error: errorManualEdit,
    isSuccess: isSuccessManualEdit,
    mutate: postManualEditDone,
  } = usePostManualEditDone();

  const [pageSize] = useState(12);
  const [currentPage, setCurrentPage] = useState(1);
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [isAddPostModalOpen, setIsAddPostModalOpen] = useState<boolean>(false);

  useEffect(() => {
    const paramsPage = searchParams.get("page");
    if (!paramsPage) return;

    if (+paramsPage !== currentPage) {
      setCurrentPage(Number(searchParams.get("page")));
    }
  }, [currentPage, searchParams]);

  useEffect(() => {
    if (
      postList?.items.length === 0 &&
      postList.total > 0 &&
      postSuccess &&
      currentPage > 1
    ) {
      setCurrentPage(Math.ceil(postList.total / postList.size));
    }
  }, [postList, currentPage, postSuccess]);

  useEffect(() => {
    navigate(
      {
        search: "?page=1",
      },
      { replace: true }
    );
  }, [
    navigate,
    sourceTypeFilter,
    postTypeFilter,
    dateFilter,
    behaviorFilter,
    keywordFilter,
    topicFilter,
  ]);

  const loadPosts = useCallback(
    (page_num: number, page_size: number) => {
      const postTypeSources: string[] = Object.keys(
        postTypeFilter ?? {}
      ).filter(
        (source) => postTypeFilter[source as keyof typeof postTypeFilter]
      );
      const sourceTypeSources: string[] = Object.keys(
        sourceTypeFilter ?? {}
      ).filter(
        (source) => sourceTypeFilter[source as keyof typeof sourceTypeFilter]
      );
      if (id) {
        let newSummaryParams: ItemsForReviewRequest = {
          report_uuid: id,
          item_type_name: [ItemTypes.SOCIAL_MEDIA_POST],
          behaviors: behaviorFilter,
          keywords: keywordFilter,
          topics: topicFilter,
          item_post_type: postTypeSources,
          item_source_name: sourceTypeSources,
          page_num,
          page_size,
        };
        if (dateFilter?.fromDate) {
          newSummaryParams = {
            ...newSummaryParams,
            min_date: dateFilter?.fromDate,
          };
        }
        if (dateFilter?.toDate) {
          newSummaryParams = {
            ...newSummaryParams,
            max_date: dateFilter?.toDate,
          };
        }
        getPosts(newSummaryParams);
      }
    },
    [
      getPosts,
      id,
      behaviorFilter,
      keywordFilter,
      topicFilter,
      postTypeFilter,
      sourceTypeFilter,
      dateFilter,
    ]
  );

  useEffect(() => {
    loadPosts(currentPage, pageSize);
  }, [id, loadPosts, currentPage, pageSize]);

  useEffect(() => {
    setPostList(initialPostList);
  }, [initialPostList, postSuccess]);

  useEffect(() => {
    if (isSuccessManualEdit) {
      navigate("/reports/amendment", { state: { stepDone: true } });
    }
  }, [isSuccessManualEdit, navigate]);

  const handleClose = useCallback(
    (createdPost?: OnCloseParams | undefined) => {
      if (createdPost) {
        loadPosts(currentPage, pageSize);
      }
      setIsAddPostModalOpen(false);
    },
    [currentPage, loadPosts, pageSize]
  );

  const handleAction = useCallback(
    (action: "edit" | "remove", post?: ItemWithMetaData) => {
      if (action === "remove") {
        loadPosts(currentPage, pageSize);
        return;
      }

      if (postList && post) {
        postList.items = postList?.items.map((item) => {
          if (item.item.external_id === post.item.external_id) {
            item.rating = post.rating;
          }
          return { ...item };
        });
        setPostList({ ...postList });
      }
    },
    [currentPage, loadPosts, pageSize, postList]
  );

  function completePostEdit() {
    if (report)
      postManualEditDone({
        reportUUID: report.report_uuid,
      });
  }

  return (
    <>
      <div className="pb-4 flex items-center gap-2">
        <span className="text-3xl font-semibold">Edit Posts</span>
        <div className="ml-auto"></div>
        <span
          className="leading-4 text-xs font-medium mr-4 text-orange-500 cursor-pointer"
          onClick={() => {
            navigate("/reports/amendment");
          }}
        >
          {"Back to List"}
        </span>
        {!isReadOnly && (
          <Button
            text={
              <span className="flex gap-3 items-center">
                <PlusIcon className="h-4" /> Add post
              </span>
            }
            type={ButtonTypes.default}
            colorType={ButtonColorTypes.cancel}
            onClick={() => {
              setIsAddPostModalOpen(true);
            }}
          />
        )}

        {!isReadOnly && (
          <Button text={"Complete Post Edit"} onClick={completePostEdit} />
        )}
      </div>
      <div className="bg-gray-50 p-4 mt-3 rounded">
        <div className="text-xl font-semibold mb-4">All Posts</div>
        {postList && (
          <AmendmentEditPosts
            behaviors={blueprint?.behaviors ?? []}
            isReadOnly={isReadOnly ?? true}
            postList={postList}
            handleAction={handleAction}
          />
        )}

        {!postsLoading && (
          <Pagination
            pageSize={pageSize}
            currentPage={currentPage}
            totalCount={postList?.total || 0}
            search={""}
          />
        )}
        <Modal
          open={isAddPostModalOpen}
          onClose={() => setIsAddPostModalOpen(false)}
          width={"w-full max-w-xl p-3"}
        >
          <AddPost report={report} onClose={handleClose} />
        </Modal>
        {(postsLoading || isLoadingManualEditDone) && <LoadingSpinner />}

        {errorManualEdit && (
          <ErrorMessageContainer message={"Error Completing Post Review"} />
        )}
      </div>
    </>
  );
};

export default PostDetails;
